import {
  Hint,
  Modal,
  PrimaryButton,
  RadioGroup,
  RadioGroupDataInterface,
  SecondaryButton,
  toast,
} from '@systemeio/ui'
import React, { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { UpdateDataFunctionType } from 'shared/hooks/use-update-data'
import { KeyedMutator } from 'swr'
import { useWorkflowActivation } from '../hooks/use-workflow-activation'
import { WorkflowApiInterface, WorkflowInterface } from '../types/workflow-interface'

interface WorkflowActivationModalProps {
  opened: boolean
  onClose: () => void
  workflow: WorkflowInterface
  updateData: UpdateDataFunctionType
  mutate: KeyedMutator<WorkflowApiInterface>
  workflows?: WorkflowApiInterface
}

enum WorkflowDeactivationEnum {
  deactivate = 'deactivate',
  deactivate_with_stop = 'deactivate_with_stop',
}

const WorkflowActivationModal = ({
  workflow,
  onClose,
  opened,
  updateData,
  mutate,
  workflows,
}: WorkflowActivationModalProps) => {
  const { t } = useLocoTranslation()

  const [isFetching, setIsFetching] = useState(false)

  const { deactivateWorkflow, deactivateWorkflowWithStop, activateWorkflow } =
    useWorkflowActivation(workflow)

  const handleConfirm = async () => {
    try {
      setIsFetching(true)
      if (!workflow.active) {
        await activateWorkflow()
      } else if (selectedOption === WorkflowDeactivationEnum.deactivate_with_stop) {
        await deactivateWorkflowWithStop()
      } else {
        await deactivateWorkflow()
      }
      await updateData(false, undefined, {
        mutationData: workflows,
        onMutate: async () => {
          await mutate(data => {
            if (data) {
              return {
                items: data.items.map(item => {
                  if (item.id === workflow.id) item = { ...item, active: !item.active }
                  return item
                }),
                hasMore: data.hasMore,
              }
            }
          }, false)
        },
      })
      toast.success(
        t('dashboard.actions.activated', {
          module: t('dashboard.workflow.title'),
          activated: workflow.active
            ? t('dashboard.actions.deactivated_caption').toLowerCase()
            : t('dashboard.actions.activated_caption').toLowerCase(),
        }),
      )
    } catch (e) {
    } finally {
      setIsFetching(false)
    }
  }

  const options: RadioGroupDataInterface<WorkflowDeactivationEnum>[] = [
    {
      id: WorkflowDeactivationEnum.deactivate,
      caption: t('dashboard.workflow.deactivate_workflow'),
      additionalInfo: (
        <Hint label={t('dashboard.workflow.deactivate_workflow_description')}>{''}</Hint>
      ),
    },
    {
      id: WorkflowDeactivationEnum.deactivate_with_stop,
      caption: t('dashboard.workflow.deactivate_workflow_with_stop'),
      additionalInfo: (
        <Hint label={t('dashboard.workflow.deactivate_workflow_with_stop_description')}>{''}</Hint>
      ),
    },
  ]

  const [selectedOption, setSelectedOption] = useState<WorkflowDeactivationEnum>(
    WorkflowDeactivationEnum.deactivate,
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('confirmation_modal.default_title')}
      isFetching={isFetching}
      isCentered={true}
      afterLeave={() => setSelectedOption(WorkflowDeactivationEnum.deactivate)}
    >
      <form className="flex flex-col gap-6 lg:gap-10">
        <div className="flex justify-center">
          <div className={'flex flex-col gap-5'}>
            <p className="text-text-primary text-base">
              {t('dashboard.actions.activate_confirmation', {
                activate: workflow.active
                  ? t('global.deactivate').toLowerCase()
                  : t('global.activate').toLowerCase(),
                module: t('dashboard.workflow.title').toLowerCase(),
              })}
            </p>
            {workflow.active && (
              <RadioGroup
                optionsWrapperClassName={'flex-col !items-start'}
                options={options}
                selected={selectedOption}
                onChange={setSelectedOption}
              />
            )}
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <SecondaryButton
            className="w-full"
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              onClose()
            }}
            disabled={isFetching}
          >
            {t('confirmation_modal.cancel')}
          </SecondaryButton>
          <PrimaryButton
            isFetching={isFetching}
            className="w-full text-base"
            width="large"
            onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              await handleConfirm()
            }}
            type="submit"
          >
            {t('confirmation_modal.confirm')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  )
}

export default WorkflowActivationModal
